import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

/**
 * Always-visible storage usage pill mounted in the topbar.
 *
 * Reads `companyDetails.media_usage` (used bytes) + `companyDetails.company
 * .allowed_media_usage` (cap in bytes) from the Redux global slice. Color
 * tiers:
 *   < 80%   neutral grey       (just informative)
 *   ≥ 80%   amber warning      (push the user to clean up or upgrade)
 *   ≥ 95%   red danger         (uploads about to fail)
 *
 * Click → /media/gallery so the user can clean up immediately.
 *
 * Renders nothing when we don't have a cap value yet (initial app boot).
 */
function formatBytes(b) {
  if (!Number.isFinite(b) || b <= 0) return '0 MB';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = b, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

export default function StoragePill() {
  const { t } = useTranslation();
  const companyDetails = useSelector((s) => s.global?.companyDetails);

  const { used, allowed, percent, tone } = useMemo(() => {
    const u = Number(companyDetails?.media_usage ?? 0);
    const a = Number(companyDetails?.company?.allowed_media_usage ?? 0);
    if (!a) return { used: 0, allowed: 0, percent: 0, tone: 'neutral' };
    const p = Math.min(100, Math.round((u / a) * 100));
    let tone = 'neutral';
    if (p >= 95)      tone = 'danger';
    else if (p >= 80) tone = 'warning';
    return { used: u, allowed: a, percent: p, tone };
  }, [companyDetails]);

  if (!allowed) return null;

  const cls = `pp-storage-pill ${tone === 'warning' ? 'is-warning' : tone === 'danger' ? 'is-danger' : ''}`;
  const icon = tone === 'danger' ? 'pi-exclamation-triangle' : tone === 'warning' ? 'pi-exclamation-circle' : 'pi-database';

  return (
    <Link to="/media/gallery" className={cls} style={{ textDecoration: 'none' }}
          title={t('storage.tooltip', { used: formatBytes(used), allowed: formatBytes(allowed), defaultValue: `${formatBytes(used)} of ${formatBytes(allowed)} used` })}>
      <i className={`pi ${icon}`} />
      <span className="pp-storage-text"><strong>{percent}%</strong> <span className="pp-storage-label">{t('storage.used', { defaultValue: 'used' })}</span></span>
    </Link>
  );
}
